import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchTrackedConnections } from '../../api/maps';
import Chevron from '../../components/Chevron';
import ConsolePanel from '../../components/ConsolePanel';
import StatusBadge from '../../components/StatusBadge';
import { C, FONT, classColor, frameSx, lcarsLabelSx } from '../../theme/tokens';
import { TrackedConnection } from '../../types';
import { trackedArrivalSx } from './vaultMotion';
import { formatServerDateTime } from '../../services/time';

/** Refetch cadence for the tracked list. The poller writes on its own clock,
 * so the page polls rather than guessing — 30s is well under the time it
 * takes anyone to read the panel and far above the write rate. */
const REFETCH_MS = 30_000;

const keyOf = (t: TrackedConnection) => `${t.from_system_id}-${t.to_system_id}`;

/**
 * The TRACKED compartment: J->J hops the location tracker recorded while
 * people were flying. Read-only on purpose — a poller must never write into
 * the curated connection list, or one bad ESI read corrupts hand-maintained
 * data. Promotion is an explicit click, mirroring the admin static-promotion
 * flow.
 */
const TrackedCompartment: React.FC<{
  mapId: number;
  onPromote: (t: TrackedConnection) => void;
  promotingKey: string | null;
}> = ({ mapId, onPromote, promotingKey }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['map-tracked', mapId],
    queryFn: () => fetchTrackedConnections(mapId),
    refetchInterval: REFETCH_MS,
  });

  // Which rows appeared *since this panel was mounted* — the arrival pulse
  // must fire for a change the user did not cause, and never on first paint
  // (where every row would pulse at once and mean nothing).
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data) return;
    const keys = new Set(data.map(keyOf));
    if (seen.current === null) {
      seen.current = keys;
      return;
    }
    const added = [...keys].filter((k) => !seen.current!.has(k));
    seen.current = keys;
    if (added.length > 0) setFresh(new Set(added));
  }, [data]);

  if (isLoading) return null;
  if (!data || data.length === 0) {
    return (
      <ConsolePanel title="Tracked" accent={C.cyan} corners={false} sx={{ mt: 3 }}>
        <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.8rem' }}>
          No jumps recorded yet. Connections appear here automatically when a character flies J&rarr;J.
        </Typography>
      </ConsolePanel>
    );
  }

  return (
    <ConsolePanel
      title="Tracked"
      accent={C.cyan}
      corners={false}
      sx={{ mt: 3 }}
      action={<StatusBadge label={`${data.length} observed`} accent={C.cyan} />}
    >
      <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), mb: 1.25 }}>
        Auto-observed — promote one to add it to this vault&rsquo;s connections
      </Typography>

      <Stack spacing={1}>
        {data.map((t) => {
          const key = keyOf(t);
          return (
            <Box
              key={key}
              sx={{
                ...frameSx(C.cyan),
                ...(fresh.has(key) ? trackedArrivalSx() : {}),
                px: 2,
                py: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Chevron color={C.cyan} />
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.text }}>{t.from.name}</Typography>
                {t.from.system_class && <StatusBadge label={t.from.system_class} accent={classColor(t.from.system_class)} />}
                <Typography sx={{ fontFamily: FONT.mono, color: C.textDim }}>&rarr;</Typography>
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.82rem', color: C.text }}>{t.to.name}</Typography>
                {t.to.system_class && <StatusBadge label={t.to.system_class} accent={classColor(t.to.system_class)} />}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>
                  {t.jump_count}&times; &middot; {formatServerDateTime(t.last_seen_at)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!t.both_saved || promotingKey === key}
                  onClick={() => onPromote(t)}
                  title={
                    t.both_saved
                      ? 'Add this connection to the vault'
                      : 'Save both systems to this vault first'
                  }
                  sx={{ fontFamily: FONT.sub, borderColor: C.borderSub, color: C.cyan, flexShrink: 0 }}
                >
                  {promotingKey === key ? 'Adding…' : 'Promote'}
                </Button>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </ConsolePanel>
  );
};

export default TrackedCompartment;
